import { isAfter, isBefore, isEqual, isSameDay, isSameMonth, isSameWeek, isSameYear } from 'date-fns';
import type { DateInput } from './types.ts';

/**
 * @see isBetween
 * @param date Date to compare
 * @param dateToCompare Date to compare with
 * @returns true if date is same day or after dateToCompare
 */
export const isSameOrAfter = (date: DateInput, dateToCompare: DateInput): boolean =>
  isSameDay(date, dateToCompare) || isAfter(date, dateToCompare);

/**
 * @see isBetween
 * @param date Date to compare
 * @param dateToCompare Date to compare with
 * @returns true if date is same day or before dateToCompare
 */
export const isSameOrBefore = (date: DateInput, dateToCompare: DateInput): boolean =>
  isSameDay(date, dateToCompare) || isBefore(date, dateToCompare);

type Inclusivity =
  | '()' // exclusive
  | '[]' // include
  | '(]' // exclude start, include end
  | '[)'; // include start, exclude end
export const isBetween = (
  date: DateInput,
  from: DateInput,
  to: DateInput,
  inclusivity: Inclusivity = '[]'
): boolean => {
  const isBeforeEqual = inclusivity[0] === '[';
  const isAfterEqual = inclusivity[1] === ']';

  return (
    (isBeforeEqual ? isEqual(from, date) || isBefore(from, date) : isBefore(from, date)) &&
    (isAfterEqual ? isEqual(to, date) || isAfter(to, date) : isAfter(to, date))
  );
};

export type IsSameCompareType = 'day' | 'week' | 'month' | 'year';
export const isSame = (date: DateInput, dateToCompare: DateInput, unit: IsSameCompareType): boolean => {
  switch (unit) {
    case 'day':
      return isSameDay(date, dateToCompare);
    case 'week':
      return isSameWeek(date, dateToCompare);
    case 'month':
      return isSameMonth(date, dateToCompare);
    case 'year':
      return isSameYear(date, dateToCompare);
    default: {
      throw new Error('Invalid unit');
    }
  }
};
